import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Formik } from 'formik';
import qs from 'qs';
import * as Yup from 'yup';

import { API_URL, get, post, put, del } from 'lib/network/api';
import FullLayout from 'layout/FullLayout';
import CardMenuProfile from './ComponentsPage/CardMenuProfile';

import InputText from 'components/InputText';
import InputSelected from 'components/InputSelected';

const OptionParticipant = [
  { id: 0, name: 'Adult', value: 'adult' },
  { id: 1, name: 'Child', value: 'child' },
];

/* ===================== API ===================== */
const fetchParticipants = async () => get(`${API_URL}/api/user/participant`);
const createParticipant = async (payload) => post(`${API_URL}/api/user/participant`, payload);
const updateParticipant = async ({ id, payload }) =>
  put(`${API_URL}/api/user/participant/${id}`, payload);
const deleteParticipant = async (id) => del(`${API_URL}/api/user/participant/${id}`);

/* ===================== Validation ===================== */
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  type: Yup.string().required('Type is required'),
});

const MyPackage = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const currentParams = qs.parse(searchParams.toString(), { comma: true });

  const [editId, setEditId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['participants', currentParams],
    queryFn: fetchParticipants,
  });

  const createMutation = useMutation({
    mutationFn: createParticipant,
    onSuccess: () => {
      queryClient.invalidateQueries(['participants']);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateParticipant,
    onSuccess: () => {
      queryClient.invalidateQueries(['participants']);
      setEditId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteParticipant,
    onSuccess: () => {
      queryClient.invalidateQueries(['participants']);
      setShowDeleteModal(false);
      setDeleteId(null);
    },
  });

  const initialValues = {
    name: '',
    phone_number: '',
    type: 'child',
  };

  const handleEdit = (participant) => {
    setEditId(participant.id);
  };

  // const handleDeleteClick = (id) => {
  //   setDeleteId(id);
  //   setShowDeleteModal(true);
  // };

  const confirmDelete = () => {
    if (deleteId) deleteMutation.mutate(deleteId);
  };

  const findValueSelected = (type) => {
    const typeParticipant = OptionParticipant.find((item) => item.value === type);
    return typeParticipant;
  };

  return (
    <div>
      <div>
        <div className="flex flex-col gap-6">
          <h2 className="text-xl font-semibold">Manage Participants</h2>

          <Formik
            enableReinitialize
            initialValues={
              editId ? data?.find((p) => p.id === editId) || initialValues : initialValues
            }
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) => {
              const payload = {
                name: values.name,
                type: values.type,
                phone_number: values.phone_number || '',
              };

              if (editId) {
                updateMutation.mutate({ id: editId, payload });
              } else {
                createMutation.mutate(payload);
              }

              resetForm();
              setEditId(null);
            }}>
            {({ handleSubmit, setFieldValue, values }) => (
              <form
                onSubmit={handleSubmit}
                className="flex bg-gray-100 flex-col gap-3 p-4 rounded-lg">
                <InputText name="name" type="text" label="Name" required />
                <InputText name="phone_number" type="text" label="Phone Number" />

                <InputSelected
                  width="w-full"
                  label="Participant Type"
                  name="type"
                  dataOption={OptionParticipant}
                  selected={findValueSelected(values.type)}
                  setSelected={(val) => setFieldValue('type', val?.value || '')}
                />

                <button type="submit" className="bg-primary-main text-white px-4 py-2 rounded mt-2">
                  {editId ? 'Update Participant' : 'Add Participant'}
                </button>
              </form>
            )}
          </Formik>

          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            {isLoading ? (
              <p className="p-4">Loading participants...</p>
            ) : isError ? (
              <p className="p-4 text-red-500">Failed to load participants.</p>
            ) : data?.length > 0 ? (
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Phone</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium w-[120px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((participant) => (
                    <tr key={participant.id} className="border-t">
                      <td className="px-4 py-2">{participant.name}</td>
                      <td className="px-4 py-2">{participant.phone_number || '-'}</td>
                      <td className="px-4 py-2 capitalize">{participant.type}</td>
                      <td className="px-4 py-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(participant)}
                          className="text-kin-text hover:underline text-xs">
                          Edit
                        </button>
                        {/* <button
                              type="button"
                              onClick={() => handleDeleteClick(participant.id)}
                              className="text-red-600 hover:underline text-xs">
                              Delete
                            </button> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="p-4 text-gray-500 text-sm">No participants found.</p>
            )}
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-2">Delete Participant</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this participant? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPackage;
